import { useState } from "react"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  ArrowDown01Icon,
  Refresh01Icon,
  Share01Icon,
} from "@hugeicons/core-free-icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Slider } from "@/components/ui/slider"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { cn } from "@/lib/utils"

type TrafficVariantSlot = {
  id: string
  label: string
  isControl?: boolean
}

/** Even split that always sums to 100. */
export function equalTrafficDistribution(count: number): number[] {
  if (count <= 0) return []
  const equal = Math.floor(100 / count)
  const remainder = 100 - equal * count
  return Array.from({ length: count }, (_, i) => equal + (i < remainder ? 1 : 0))
}

/**
 * Rescale weights so they sum to exactly 100 while preserving proportions.
 * Falls back to an even split when the input carries no weight to scale from.
 */
export function renormalizeTrafficDistribution(weights: number[]): number[] {
  if (weights.length === 0) return []
  const total = weights.reduce((a, b) => a + b, 0)
  if (total <= 0) return equalTrafficDistribution(weights.length)
  const normalized = weights.map((v) => Math.round((v / total) * 100))
  const diff = 100 - normalized.reduce((a, b) => a + b, 0)
  if (diff !== 0) {
    const maxIdx = normalized.indexOf(Math.max(...normalized))
    normalized[maxIdx] += diff
  }
  return normalized
}

/**
 * Resize a distribution to `count` slots while keeping totals at 100.
 * A matching length whose weights drifted off 100 (e.g. after a weighted variant
 * was removed) is renormalized proportionally so a custom split survives; shrinking
 * renormalizes the kept prefix; expanding falls back to an even split so newly added
 * page variants don't inherit a leftover from a prior element test.
 */
export function normalizeTrafficDistribution(
  current: number[],
  count: number
): number[] {
  if (count <= 0) return []
  if (current.length === count) {
    const total = current.reduce((a, b) => a + b, 0)
    return total === 100 ? current : renormalizeTrafficDistribution(current)
  }
  if (current.length > count) {
    return renormalizeTrafficDistribution(current.slice(0, count))
  }
  return equalTrafficDistribution(count)
}

type TrafficDistributionPanelProps = {
  variants: TrafficVariantSlot[]
  distribution: number[]
  onChange: (next: number[]) => void
  defaultOpen?: boolean
}

/**
 * Shared traffic-split control for page-level and no-code element tests.
 * Same optional collapsible pattern as visitor assignment on the Variants step.
 */
export function TrafficDistributionPanel({
  variants,
  distribution,
  onChange,
  defaultOpen = false,
}: TrafficDistributionPanelProps) {
  const [open, setOpen] = useState(defaultOpen)
  const summary = distribution.map((p) => `${p}%`).join(" / ")

  const updateSlot = (idx: number, value: number) => {
    const next = [...distribution]
    next[idx] = value
    if (next.reduce((a, b) => a + b, 0) <= 0) return
    onChange(renormalizeTrafficDistribution(next))
  }

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="border border-border/30 rounded-lg overflow-hidden bg-card"
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="w-full flex items-center justify-between p-4 hover:bg-muted/20 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-muted/40 border border-border/40">
              <HugeiconsIcon
                icon={Share01Icon}
                className="h-4 w-4 text-muted-foreground"
              />
            </div>
            <div className="text-left">
              <p className="text-xs font-medium">Custom traffic distribution</p>
              <p className="text-[11px] text-muted-foreground/70">
                {open
                  ? "Adjust how traffic is split between variants"
                  : `Currently: ${summary}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              variant="secondary"
              className="text-[11px] font-medium px-1.5 py-0 h-4"
            >
              Optional
            </Badge>
            <HugeiconsIcon
              icon={ArrowDown01Icon}
              className={cn(
                "h-3.5 w-3.5 text-muted-foreground transition-transform",
                open && "rotate-180"
              )}
            />
          </div>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="p-3 pt-0 space-y-3 border-t border-border/50 bg-muted/10">
          <div className="space-y-2.5 pt-3">
            {variants.map((variant, idx) => (
              <div key={variant.id} className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={variant.isControl ? "outline" : "secondary"}
                      className="text-[11px] font-medium px-1.5 py-0 h-4"
                    >
                      {variant.id}
                    </Badge>
                    <span className="font-medium">
                      {variant.label ||
                        (variant.isControl ? "Control" : "Variant")}
                    </span>
                  </div>
                  <span className="text-muted-foreground font-medium tabular-nums">
                    {distribution[idx] ?? 0}%
                  </span>
                </div>
                <Slider
                  value={[distribution[idx] ?? 0]}
                  onValueChange={([value]) => updateSlot(idx, value)}
                  min={0}
                  max={100}
                  step={1}
                  className="w-full"
                />
              </div>
            ))}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onChange(equalTrafficDistribution(variants.length))}
            className="w-full h-8 text-xs font-medium"
          >
            <HugeiconsIcon
              icon={Refresh01Icon}
              size={12}
              className="h-3 w-3 mr-1.5"
            />
            Reset to Equal Distribution
          </Button>
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
